import { useEffect, useRef, useState } from "react";
import { Button, Spinner, TextInput } from "flowbite-react";
import {
  HiCheck,
  HiOutlineChatBubbleBottomCenterText,
  HiOutlineInformationCircle,
  HiOutlinePlus,
  HiOutlineTrash,
} from "react-icons/hi2";
import { FiTarget } from "react-icons/fi";

import { useCustomerGoals, useToast, PROGRESS_STEPS } from "../../../hooks";
import { NOTIFICATION_TYPES } from "../../../utils";

const isReached = (goal) => (goal.progress_pct ?? 0) >= 100;

const formatReached = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  return `Reached ${date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
};

/** Single goal row — checkbox, title, progress bar, % picker, note + delete. */
const GoalRow = ({
  goal,
  onSetProgress,
  onToggleReached,
  onSaveNote,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(goal.note ?? "");
  const [savingNote, setSavingNote] = useState(false);

  const reached = isReached(goal);
  const hasNote = (goal.note || "").trim().length > 0;
  const isHubspot = goal.source === "hubspot";
  const noteDirty = (noteDraft ?? "") !== (goal.note ?? "");
  const iconBtn =
    "flex size-7 shrink-0 items-center justify-center rounded-md border border-transparent transition-colors";

  const openNote = () => {
    setNoteDraft(goal.note ?? ""); // start from the latest saved value
    setOpen(true);
  };

  const closeNote = () => {
    setNoteDraft(goal.note ?? ""); // discard unsaved edits
    setOpen(false);
  };

  const saveNote = async () => {
    if (!noteDirty) {
      setOpen(false);
      return;
    }
    setSavingNote(true);
    try {
      await onSaveNote(goal.id, noteDraft); // parent toasts success/error
      setOpen(false);
    } catch {
      // Save failed — parent surfaced the error; keep the editor open to retry.
    } finally {
      setSavingNote(false);
    }
  };

  const handleNoteKey = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeNote();
    }
    // ⌘/Ctrl + Enter saves without reaching for the mouse.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      saveNote();
    }
  };

  return (
    <div className="border-t border-gray-100">
      <div className="flex items-center gap-3 px-0.5 py-3">
        {/* Complete toggle — flips the BE `reached` flag (100% ↔ 0%) */}
        <button
          type="button"
          role="checkbox"
          aria-checked={reached}
          onClick={() => onToggleReached(goal.id, !reached)}
          title="Mark complete"
          className={`flex size-[22px] shrink-0 items-center justify-center rounded-full border transition-colors ${
            reached
              ? "border-emerald-500 bg-emerald-500 text-white"
              : "border-gray-300 bg-white hover:border-emerald-500"
          }`}
        >
          {reached && <HiCheck className="size-3.5" />}
        </button>

        {/* Title + reached date */}
        <div className="flex min-w-0 flex-1 items-baseline gap-2.5">
          <span
            className={`truncate text-sm font-semibold ${
              reached ? "text-gray-400 line-through" : "text-gray-900"
            }`}
          >
            {goal.title}
          </span>
          {isHubspot && (
            <span className="shrink-0 rounded bg-orange-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-600">
              HubSpot
            </span>
          )}
          {reached && goal.reached_at && (
            <span className="shrink-0 text-xs text-gray-400">
              {formatReached(goal.reached_at)}
            </span>
          )}
        </div>

        {/* Controls */}
        <div className="flex shrink-0 items-center gap-3">
          {/* Progress bar */}
          <div className="hidden h-1.5 w-32 overflow-hidden rounded-full bg-gray-100 sm:block lg:w-40">
            <div
              className={`h-full rounded-full transition-all ${
                reached ? "bg-emerald-500" : "bg-blue-500"
              }`}
              style={{ width: `${goal.progress_pct ?? 0}%` }}
            />
          </div>

          {/* Discrete percentage picker (BE enum) */}
          <div className="relative inline-flex items-center">
            <select
              value={String(goal.progress_pct ?? 0)}
              onChange={(e) =>
                onSetProgress(goal.id, parseInt(e.target.value, 10))
              }
              title="Set progress"
              aria-label={`Progress for ${goal.title}`}
              className={`min-w-[56px] cursor-pointer appearance-none rounded-md border-none bg-transparent py-0.5 pl-1.5 pr-5 text-right text-sm font-bold outline-none focus:ring-0 ${
                reached ? "text-emerald-600" : "text-blue-600"
              }`}
            >
              {PROGRESS_STEPS.map((v) => (
                <option key={v} value={v}>
                  {v}%
                </option>
              ))}
            </select>
          </div>

          <div className="h-5 w-px bg-gray-200" />

          {/* Note toggle */}
          <button
            type="button"
            onClick={() => (open ? closeNote() : openNote())}
            title={hasNote ? "Edit note" : "Add note"}
            aria-label={`Add note to ${goal.title}`}
            aria-expanded={open}
            className={`${iconBtn} ${
              hasNote
                ? "bg-blue-50 text-blue-600"
                : "text-gray-400 hover:bg-gray-50"
            }`}
          >
            <HiOutlineChatBubbleBottomCenterText className="size-[17px]" />
          </button>

          {/* Delete — hubspot-sourced goals can't be deleted (BE 403) */}
          <button
            type="button"
            onClick={() => onDelete(goal.id)}
            disabled={isHubspot}
            title={
              isHubspot ? "Synced from HubSpot — can't delete" : "Delete goal"
            }
            aria-label={`Delete ${goal.title}`}
            className={`${iconBtn} ${
              isHubspot
                ? "cursor-not-allowed text-gray-200"
                : "text-gray-400 hover:bg-red-50 hover:text-red-600"
            }`}
          >
            <HiOutlineTrash className="size-4" />
          </button>
        </div>
      </div>

      {/* Note editor */}
      {open && (
        <div className="pb-3.5 pl-[35px] pr-0.5">
          <textarea
            value={noteDraft}
            autoFocus
            onChange={(e) => setNoteDraft(e.target.value)}
            onKeyDown={handleNoteKey}
            placeholder="Add context — evidence, blockers, or the next step for this goal…"
            className="min-h-[62px] w-full resize-y rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-[13px] leading-relaxed text-gray-700 focus:border-blue-500 focus:ring-blue-500"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-[11px] text-gray-400">
              ⌘/Ctrl+Enter to save · Esc to cancel
            </span>
            <div className="flex items-center gap-2">
              <Button size="xs" color="light" onClick={closeNote}>
                Cancel
              </Button>
              <Button
                size="xs"
                color="blue"
                onClick={saveNote}
                disabled={!noteDirty || savingNote}
                isProcessing={savingNote}
              >
                Save note
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const GoalsSkeleton = () => (
  <div className="flex items-center gap-2 px-5 py-8 text-sm text-gray-400">
    <Spinner size="sm" /> Loading goals…
  </div>
);

/**
 * Filter options by case-insensitive substring on `label`.
 * Secondary sort: startswith wins over contains.
 */
const filterOptions = (opts, query) => {
  if (!query) return opts;
  const q = query.toLowerCase();
  const starts = [];
  const contains = [];
  for (const opt of opts) {
    const lbl = opt.label.toLowerCase();
    if (lbl.startsWith(q)) starts.push(opt);
    else if (lbl.includes(q)) contains.push(opt);
  }
  return [...starts, ...contains];
};

/**
 * Inline combobox for enum-kind goals teams.
 *
 * - Text input shows filtered option rows in an overlay list.
 * - ↑/↓ to move highlight; Enter to pick highlighted or submit free text; Esc to cancel.
 * - Clicking an option submits immediately.
 * - If EVERY option's `category` is null/empty, the right column is hidden.
 */
const GoalCombobox = ({ options, onSubmit, onCancel, optionsLoading }) => {
  const [draft, setDraft] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = "goal-combobox-listbox";

  // Derived state
  const isLoading = optionsLoading;
  const filtered = filterOptions(options, draft);
  const isOpen = !isLoading && filtered.length > 0;

  // Whether any option carries a category — drives right-column visibility.
  const hasCategories = options.some((o) => o.category);

  // Keep activeIdx in bounds when the filtered list shrinks.
  useEffect(() => {
    setActiveIdx(-1);
  }, [draft]);

  // Scroll active item into view.
  useEffect(() => {
    if (activeIdx < 0 || !listRef.current) return;
    const item = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    item?.scrollIntoView?.({ block: "nearest" });
  }, [activeIdx]);

  const activeOption = activeIdx >= 0 ? filtered[activeIdx] : null;
  const activeId = activeOption ? `goal-opt-${activeIdx}` : undefined;

  const submit = (opt) => {
    if (opt) {
      onSubmit({ title: opt.label, option_value: opt.value });
    } else {
      const title = draft.trim();
      if (!title) return;
      onSubmit({ title });
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter") {
        e.preventDefault();
        submit(null);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      submit(activeOption);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  const placeholder = isLoading
    ? "Loading goal library…"
    : "Search the goal library, or describe your own goal";

  return (
    <div className="relative">
      <div className="flex items-center gap-2.5">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listboxId : undefined}
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            autoFocus
            disabled={isLoading}
            value={draft}
            maxLength={500}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
          />

          {/* Overlay list */}
          {isOpen && (
            <div
              id={listboxId}
              ref={listRef}
              role="listbox"
              aria-label="Goal library"
              className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
            >
              {/* Eyebrow header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-3.5 py-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Goal library
                </span>
                <span className="text-[11px] text-gray-400">
                  {filtered.length} of {options.length}
                </span>
              </div>

              {filtered.map((opt, idx) => {
                const isActive = idx === activeIdx;
                return (
                  <div
                    key={opt.value}
                    id={`goal-opt-${idx}`}
                    role="option"
                    aria-selected={isActive}
                    data-idx={idx}
                    onMouseDown={(e) => {
                      // Prevent blur on the input before the click fires.
                      e.preventDefault();
                      submit(opt);
                    }}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={`flex cursor-pointer items-center gap-2 px-3.5 py-2.5 text-sm ${
                      isActive ? "bg-blue-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <HiOutlinePlus
                      className={`size-3.5 shrink-0 ${
                        isActive ? "text-blue-500" : "text-gray-400"
                      }`}
                    />
                    <span
                      className={`min-w-0 flex-1 truncate font-semibold ${
                        isActive ? "text-blue-700" : "text-gray-900"
                      }`}
                    >
                      {opt.label}
                    </span>
                    {hasCategories && opt.category && (
                      <span className="shrink-0 text-xs text-gray-400">
                        {opt.category}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Button
          size="xs"
          color="blue"
          disabled={isLoading || !draft.trim()}
          onClick={() => submit(activeOption)}
        >
          Add goal
        </Button>
        <Button size="xs" color="light" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

/**
 * Customer goals — "what success looks like" for an account.
 *
 * Wired to `accounts/{accountId}/customer-goals/` via {@link useCustomerGoals}.
 *
 * @param {string} accountId    - Account UUID.
 * @param {string} customerName - Account name, used in the copy.
 */
export const CustomerGoalsSection = ({ accountId, customerName }) => {
  const showToast = useToast();
  const {
    goals,
    loading,
    error,
    reachedCount,
    totalCount,
    atCap,
    cap,
    addGoal,
    updateGoal,
    deleteGoal,
    enumSource,
    optionsLoading,
  } = useCustomerGoals(accountId);

  const [adding, setAdding] = useState(false);
  // draft is only used for the text/null free-text path.
  const [draft, setDraft] = useState("");

  const allReached = totalCount > 0 && reachedCount === totalCount;

  // Render the combobox whenever enumSource is configured (even when textSource
  // is also present — the combobox already accepts free text via typing, which
  // becomes the text-source POST path).
  const showCombobox = enumSource != null;

  const toastError = (content) =>
    showToast({ content, type: NOTIFICATION_TYPES.error });

  const handleSetProgress = (goalId, progressPct) =>
    updateGoal(goalId, { progress_pct: progressPct }).catch(() =>
      toastError("Couldn't update progress. Please try again."),
    );

  const handleToggleReached = (goalId, reached) =>
    updateGoal(goalId, { reached }).catch(() =>
      toastError("Couldn't update goal. Please try again."),
    );

  const handleSaveNote = (goalId, note) =>
    updateGoal(goalId, { note }).then(
      (res) => {
        showToast({ content: "Note saved.", type: NOTIFICATION_TYPES.success });
        return res;
      },
      (err) => {
        toastError("Couldn't save your note. Please try again.");
        throw err; // let the editor stay open so the user can retry
      },
    );

  const handleDelete = (goalId) =>
    deleteGoal(goalId).catch((err) =>
      toastError(
        err.response?.status === 403
          ? "Goals synced from HubSpot can't be deleted."
          : "Couldn't delete the goal. Please try again.",
      ),
    );

  const startAdd = () => {
    setDraft("");
    setAdding(true);
  };

  const cancelAdd = () => {
    setDraft("");
    setAdding(false);
  };

  // Shared submit handler used by both the free-text path and the combobox.
  // Accepts { title, option_value? }.
  const commitAdd = async ({ title, option_value } = {}) => {
    const trimmedTitle = (title ?? "").trim();
    if (!trimmedTitle) return;
    try {
      await addGoal({ title: trimmedTitle, option_value });
      setDraft("");
      setAdding(false);
    } catch (err) {
      toastError(
        err.response?.data?.detail ??
          "Couldn't add the goal. Please try again.",
      );
    }
  };

  // Free-text path key handler (kind === null | "text").
  const handleDraftKey = (e) => {
    if (e.key === "Enter") commitAdd({ title: draft });
    if (e.key === "Escape") cancelAdd();
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-5 py-4">
        <FiTarget className="size-5 text-blue-600" />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-gray-900">
            Customer goals
          </h2>
          {customerName && (
            <p className="mt-0.5 text-xs text-gray-500">
              What success looks like for {customerName}
            </p>
          )}
        </div>

        {totalCount > 0 && (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
              allReached
                ? "bg-emerald-50 text-emerald-700"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            {reachedCount} of {totalCount} reached
          </span>
        )}

        <Button
          size="xs"
          color="light"
          onClick={startAdd}
          disabled={atCap || adding || loading || !!error}
          title={atCap ? `Maximum ${cap} goals per customer` : undefined}
        >
          <HiOutlinePlus className="mr-1.5 size-4" />
          Add goal
        </Button>
      </div>

      {loading ? (
        <GoalsSkeleton />
      ) : error ? (
        <div className="px-5 py-6">
          <p className="text-sm text-gray-500">
            {error === "not_found"
              ? "Goals aren't available for this customer."
              : "Couldn't load customer goals."}
          </p>
        </div>
      ) : (
        <div className="px-5 py-4">
          {/* Add goal composer */}
          {adding && (
            <div className="mb-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3">
              {showCombobox ? (
                <GoalCombobox
                  options={enumSource.options}
                  optionsLoading={optionsLoading}
                  onSubmit={commitAdd}
                  onCancel={cancelAdd}
                />
              ) : (
                <div className="flex items-center gap-2.5">
                  <TextInput
                    className="flex-1"
                    value={draft}
                    autoFocus
                    maxLength={500}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleDraftKey}
                    placeholder="Describe the goal — e.g. Reduce onboarding time to under 2 weeks"
                  />
                  <Button
                    size="xs"
                    color="blue"
                    onClick={() => commitAdd({ title: draft })}
                    disabled={!draft.trim()}
                  >
                    Add goal
                  </Button>
                  <Button size="xs" color="light" onClick={cancelAdd}>
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {totalCount === 0 ? (
            <div className="flex items-start gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-5">
              <HiOutlineInformationCircle className="mt-0.5 size-5 shrink-0 text-gray-400" />
              <p className="text-sm text-gray-500">
                <span className="font-semibold text-gray-700">
                  No goals agreed yet.
                </span>{" "}
                Add 1–2 measurable goals that define the bar for a successful
                customer relationship.
              </p>
            </div>
          ) : (
            <div>
              {goals.map((goal) => (
                <GoalRow
                  key={goal.id}
                  goal={goal}
                  onSetProgress={handleSetProgress}
                  onToggleReached={handleToggleReached}
                  onSaveNote={handleSaveNote}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
